import React, { useEffect, useState } from 'react';
import { legacyApi, DesignMixModel, GRCType } from '../services/legacyApi';

interface DesignMixModalProps {
    isOpen: boolean;
    onClose: () => void;
    estimateId: string;
    onSave: (designMixId: number, grcTypeId: number) => void;
    currentDesignMixId?: number | null;
    currentGrcTypeId?: number | null;
}

export const DesignMixModal: React.FC<DesignMixModalProps> = ({
    isOpen, onClose, estimateId, onSave,
    currentDesignMixId, currentGrcTypeId
}) => {
    const [models, setModels] = useState<DesignMixModel[]>([]);
    const [types, setTypes] = useState<GRCType[]>([]);
    const [selectedModel, setSelectedModel] = useState<number | null>(currentDesignMixId || null);
    const [selectedType, setSelectedType] = useState<number | null>(currentGrcTypeId || null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSelectedModel(currentDesignMixId || null);
            setSelectedType(currentGrcTypeId || null);
            setLoading(true);
            Promise.all([
                legacyApi.getDesignMixModels(),
                legacyApi.getGRCTypes()
            ]).then(([fetchedModels, fetchedTypes]) => {
                setModels(fetchedModels);
                // Filter unique types by name to prevent duplicates
                // Also ensure we accept whatever the backend gives us, trusting api.
                const uniqueTypes = Array.from(new Map(fetchedTypes.map(item => [item.name, item])).values());
                setTypes(uniqueTypes);
                setLoading(false);
            }).catch(err => {
                console.error("Failed to load design mix data", err);
                setLoading(false);
            });
        }
    }, [isOpen, currentDesignMixId, currentGrcTypeId]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center -z-50" style={{ zIndex: 9999 }}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden font-sans">
                {/* Header */}
                <div className="bg-gray-800 text-white px-4 py-3 flex justify-between items-center">
                    <h3 className="text-lg font-semibold">Estimate</h3>
                    <button onClick={onClose} className="text-white hover:text-gray-300">Close</button>
                </div>

                <div className="p-6 space-y-4">
                    {/* ID Field */}
                    <div className="flex items-center">
                        <label className="w-1/3 text-sm font-semibold bg-blue-100 p-2 border">ID</label>
                        <input
                            type="text"
                            value={estimateId}
                            disabled
                            className="w-2/3 p-2 border bg-gray-50 text-gray-600"
                        />
                    </div>

                    {/* Design Mix Model */}
                    <div className="flex items-center">
                        <label className="w-1/3 text-sm font-semibold bg-blue-100 p-2 border">Design Mix Model</label>
                        <select
                            className="w-2/3 p-2 border"
                            value={selectedModel || ''}
                            onChange={(e) => setSelectedModel(Number(e.target.value))}
                        >
                            <option value="">Select...</option>
                            {models.map(model => (
                                <option key={model.id} value={model.id}>{model.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* GRC Type */}
                    <div className="flex items-center">
                        <label className="w-1/3 text-sm font-semibold bg-blue-100 p-2 border">GRC Type</label>
                        <select
                            className="w-2/3 p-2 border"
                            value={selectedType || ''}
                            onChange={(e) => setSelectedType(Number(e.target.value))}
                        >
                            <option value="">Select...</option>
                            {types.map(type => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Footer Buttons */}
                    {/* Assuming auto-save or explicit save? Legacy usually auto-saves on close or main save. 
                        But specific modals usually have actions. Screenshot shows 'Close' but logic implies selection. 
                        I'll add a hidden save logic if needed, but for now just display. 
                        Wait, screenshot buttons just 'Close' or 'Estimate' (Title). 
                        Actually, 'Select Design Mix' opens this. So user selects and closes? 
                        I'll add a 'Update' button just in case. */}
                </div>
                <div className="bg-gray-100 p-3 flex justify-end gap-2">
                    <button
                        onClick={() => {
                            setSelectedModel(null);
                            setSelectedType(null);
                            onSave(0, 0);
                        }}
                        className="bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200 transition-colors"
                    >
                        Clear
                    </button>
                    <button
                        onClick={() => {
                            if (selectedModel && selectedType) onSave(selectedModel, selectedType);
                            onClose();
                        }}
                        className="bg-gray-800 text-white px-4 py-2 rounded hover:bg-gray-700"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};
